<template>
  <div class="meal-reservation-form">
    <div class="form-card">
      <div v-if="loading" class="loading-wrap">
        <van-loading type="spinner" vertical>加载中...</van-loading>
      </div>
      <form style="padding: 12px 0" v-else @submit.prevent="onSubmit">
        <!-- 送餐地点 -->
        <van-cell
          title="送餐地点"
          :value="formData.deliveryAddressText || '请选择送餐地点'"
          :class="{ 'cell-placeholder': !formData.deliveryAddressText }"
          :is-link="!isEdit"
          @click="isEdit ? null : (showDeliveryAddressPicker = true)"
        />
        <!-- 送餐书院 -->
        <van-cell
          v-if="formData.diningSchoolText"
          title="送餐书院"
          :value="formData.diningSchoolText"
          :class="{ 'cell-placeholder': !formData.diningSchoolText }"
        />
        <!-- 送餐日期（多选） -->
        <van-cell
          title="送餐日期"
          :value="formData.plannedDiningTimesText || '请选择日期'"
          :class="{ 'cell-placeholder': !formData.plannedDiningTimesText }"
          :is-link="!isEdit"
          @click="isEdit ? null : (showPlannedDiningTimesPicker = true)"
          class="multi-line-cell"
        />
        <!-- 餐次 -->
        <van-cell
          title="餐次"
          :value="formData.mealPeriodText || '请选择餐次'"
          :class="{ 'cell-placeholder': !formData.mealPeriodText }"
          :is-link="!isEdit"
          @click="isEdit ? null : (showMealPeriodPicker = true)"
        />
        <!-- 数量 -->
        <van-cell title="数量">
          <template #value>
            <div class="quantity-input">
              <button
                type="button"
                class="quantity-btn"
                @click="changeQuantity(-1)"
              >
                -
              </button>
              <div class="quantity-divider"></div>
              <input
                type="text"
                v-model="formData.quantity"
                class="quantity-field"
                maxlength="3"
                pattern="[0-9]*"
                inputmode="numeric"
                @input="onQuantityInput"
              />
              <div class="quantity-divider"></div>
              <button
                type="button"
                class="quantity-btn"
                @click="changeQuantity(1)"
              >
                +
              </button>
            </div>
          </template>
        </van-cell>
        <!-- 套餐 -->
        <van-cell
          title="套餐"
          :value="formData.mealPackageText || '请选择套餐'"
          :class="{ 'cell-placeholder': !formData.mealPackageText }"
          is-link
          @click="handleChoiceMealPackage"
        />
      </form>
    </div>
    <!-- van-popup 统一放到外部 -->
    <van-popup
      v-model:show="showDeliveryAddressPicker"
      position="bottom"
      teleport="body"
    >
      <van-picker
        :columns="deliveryAddressColumns"
        @confirm="onDeliveryAddressConfirm"
        @cancel="showDeliveryAddressPicker = false"
      />
    </van-popup>
    <van-calendar
      v-model:show="showPlannedDiningTimesPicker"
      type="multiple"
      :min-date="minDate"
      :max-date="maxDate"
      :default-date="formData.plannedDiningTimes"
      :formatter="calendarDayFormatter"
      @confirm="onPlannedDiningTimesConfirm"
      @cancel="showPlannedDiningTimesPicker = false"
    />
    <van-popup
      v-model:show="showMealPeriodPicker"
      position="bottom"
      teleport="body"
    >
      <van-picker
        :columns="mealPeriodColumns"
        @confirm="onMealPeriodConfirm"
        @cancel="showMealPeriodPicker = false"
      />
    </van-popup>
    <van-popup
      v-model:show="showMealPackagePicker"
      position="bottom"
      teleport="body"
    >
      <van-picker
        :columns="mealPackageColumns"
        @confirm="onMealPackageConfirm"
        @cancel="showMealPackagePicker = false"
      />
    </van-popup>
    <!-- 套餐说明示例 -->
    <div class="meal-desc">
      <div class="meal-list" v-for="(item, index) in mealDescList" :key="index">
        {{ item }}
      </div>
    </div>
    <div class="reservation-tip">
      温馨提示：
      <br>
      1.当日{{ state.limitTime }}点之前，可预约T+1及以后日期送餐;
      <br>
      2.支持多日期、同餐次、同套餐批量下单，不同餐次、套餐请分开下单;
      <br>
      3.同餐同套餐不能重复下单，增减数量请到订单页面操作;
    </div>
    <div class="form-btn-wrap">
      <button
        class="submit-btn"
        :disabled="submitting"
        type="submit"
        @click="onSubmit"
      >
        {{ isEdit ? "保存" : "提交" }}
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, ref, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { showNotify, showDialog } from "vant";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);
import {
  createMealReservation,
  updateMealReservation,
  queryMealPackage,
  queryMealStandardList,
  getDisabledDatesAPI
} from "@/api/meal";
import { postSyparamSelectList, getSyOrgSendPlace } from "@/api/report";
import { querySelectUserById } from "@/api/user";
export default defineComponent({
  name: "MealReservationForm",

  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = ref(!!route.query.id);
    const submitting = ref(false);
    const loading = ref(false);

    const state = reactive({
      userId: null,
      realName: null,
      phone: null,
      limitTime: "11:00",
    });
    // 日期选择器配置
    const showDatePicker = ref(false);
    const minDate = dayjs().add(1, "day").toDate();
    // 计算下周日
    const today = dayjs();
    const daysToSunday = 7 - today.day();
    const thisSunday = today.add(daysToSunday, "day");
    const nextSunday = thisSunday.add(7, "day");
    const maxDate = nextSunday.toDate();

    // 新增：不可选日期
    const disabledDates = ref([]);
    // mock 获取不可选日期接口
    const fetchDisabledDates = async () => {
      // 生成 minDate 到 maxDate 的所有日期
      const start = dayjs(minDate);
      const end = dayjs(maxDate);
      const schedulingDates = [];
      let cur = start.clone();
      while (cur.isSameOrBefore(end, 'day')) {
        schedulingDates.push(cur.format('YYYY-MM-DD'));
        cur = cur.add(1, 'day');
      }
      const res = await getDisabledDatesAPI({
        schedulingDates,
        orgId: formData.orgId
      });
      disabledDates.value = res.data.filter(item =>!item.hasNoSideDish).map(item => item.schedulingDate);
      console.log('res.data>>>>>')
      
      console.log(disabledDates.value)
    };
    // 日历自定义渲染，禁用不可选日期
    const calendarDayFormatter = (day) => {
      const dateStr = day.date ? dayjs(day.date).format("YYYY-MM-DD") : "";
      if (disabledDates.value.includes(dateStr)) {
        return { ...day, type: "disabled" };
      }
      return day;
    };

    // 各种选择器的显示状态
    const showMealTypePicker = ref(false);
    const showMealNamePicker = ref(false);
    const showLocationPicker = ref(false);

    const showDeliveryAddressPicker = ref(false);
    const showDiningSchoolPicker = ref(false);
    const showPlannedDiningTimesPicker = ref(false);
    const showMealPeriodPicker = ref(false);
    const showMealPackagePicker = ref(false);

    const choiceTypes = ref(null);

    const mealDescList = ref([]);

    // 选项数据
    const mealTypeColumns = [
      //   { text: "早餐", value: "1" },
      { text: "午餐", value: "2" },
      { text: "晚餐", value: "3" },
      //   { text: "夜宵", value: "4" },
    ];

    const mealNameColumns = [];

    const locationColumns = [];

    // 送餐地点选项（接口获取）
    const deliveryAddressColumns = ref([]);
    // 送餐书院选项
    const diningSchoolColumns = [];
    // 餐次选项
    const mealPeriodColumns = [
      //   { text: "早餐", value: "1" },
      { text: "午餐", value: "2" },
      { text: "晚餐", value: "3" },
      //   { text: "夜宵", value: "4" },
    ];
    // 套餐选项
    const mealPackageColumns = ref([]);

    // 表单数据
    const formData = reactive({
      deliveryAddress: "",
      deliveryAddressText: "",
      diningSchool: "",
      diningSchoolText: "",
      plannedDiningTimes: [], // 多选日期，必须为数组
      mealPeriod: 2,
      mealPeriodText: "午餐",
      quantity: 1,
      mealPackageId: "",
      mealPackageText: "",
      plannedDiningTimesText: "",
      orgId: "",
    });

    // 获取预约详情
    const getReservationDetail = (info) => {
      // 送餐地点
      console.log("编辑>>>>>>", info);
      formData.id = info.id;
      formData.quantity = info.quantity ? info.quantity : 1;
      formData.mealPackageId = info.mealPackageId || "";
      formData.mealPackageText = info.mealPackage || "";
      formData.orgId = info.orgId || "";
      formData.deliveryAddressText = info.deliveryAddress || "";
      formData.deliveryAddress = deliveryAddressColumns.value.find(
        (item) => item.text == info.deliveryAddress
      )?.value;
      formData.mealPeriod = info.mealPeriod || "";
      formData.mealPeriodText = mealPeriodColumns.find(
        (item) => item.value == info.mealPeriod
      )?.text;
      formData.plannedDiningTimes = [
        dayjs(info.plannedDiningTime).format("YYYY-MM-DD"),
      ];
      formData.plannedDiningTimesText = dayjs(info.plannedDiningTime).format(
        "YYYY-MM-DD"
      );

      getSyOrgSendPlace({ id: info.orgId }).then((res) => {
        formData.diningSchoolText = res.data.name;
        formData.diningSchool = info.orgId;
        getMealNames();
      });
    };

    // 送餐地点选择确认
    const onDeliveryAddressConfirm = ({ selectedOptions }) => {
      formData.deliveryAddress = selectedOptions?.[0]?.value;
      formData.deliveryAddressText = selectedOptions?.[0]?.text;

      formData.orgId = selectedOptions?.[0]?.orgID;

      showDeliveryAddressPicker.value = false;
      formData.mealPackageId = "";
      formData.mealPackageText = "";
      mealDescList.value = [];
      getMealNames();

      getSyOrgSendPlace({ id: selectedOptions?.[0]?.orgID }).then((res) => {
        formData.diningSchoolText = res.data.name;
        formData.diningSchool = selectedOptions?.[0]?.orgID;
      });
    };
    // 送餐书院选择确认
    const onDiningSchoolConfirm = ({ selectedOptions }) => {
      formData.diningSchool = selectedOptions?.[0]?.value;
      formData.diningSchoolText = selectedOptions?.[0]?.text;
      showDiningSchoolPicker.value = false;
    };
    // 餐次选择确认
    const onMealPeriodConfirm = ({ selectedOptions }) => {
      console.log("value?>???", selectedOptions);
      formData.mealPeriod = selectedOptions?.[0]?.value;
      formData.mealPeriodText = selectedOptions?.[0]?.text;
      showMealPeriodPicker.value = false;
      formData.mealPackageId = "";
      formData.mealPackageText = "";
      mealDescList.value = [];
      getMealNames();
    };
    // 套餐选择确认
    const onMealPackageConfirm = ({ selectedOptions }) => {
      formData.mealPackageId = selectedOptions?.[0]?.value;
      formData.mealPackageText = selectedOptions?.[0]?.text;
      choiceTypes.value = selectedOptions?.[0]?.type;
      showMealPackagePicker.value = false;
      console.log("selectedOptions>>>>>", selectedOptions);
      if (selectedOptions[0]) {
        getMealStandardList();
      }
    };

    const onPlannedDiningTimesConfirm = (value) => {
      // 1. 保存选中的日期数组
      formData.plannedDiningTimes = value;
      // 2. 格式化为字符串，换行拼接
      formData.plannedDiningTimesText = value
        .map((date) => dayjs(date).format("YYYY-MM-DD"))
        .join("\n");
      // 3. 关闭弹窗
      showPlannedDiningTimesPicker.value = false;
      formData.mealPackageId = "";
      formData.mealPackageText = "";
      mealDescList.value = [];
      getMealNames();
    };

    const changeQuantity = (delta) => {
      let val = parseInt(formData.quantity, 10) || 1;
      val += delta;
      if (val < 1) val = 1;
      formData.quantity = val.toString();
    };

    // 提交表单
    const onSubmit = async () => {
      // 必填项校验
      if (!formData.deliveryAddressText) {
        showNotify({ type: "warning", message: "请选择送餐地点" });
        return;
      }
      if (
        !formData.plannedDiningTimes ||
        formData.plannedDiningTimes.length === 0
      ) {
        showNotify({ type: "warning", message: "请选择送餐日期" });
        return;
      }
      if (!formData.mealPeriodText) {
        showNotify({ type: "warning", message: "请选择餐次" });
        return;
      }
      if (
        !formData.quantity ||
        isNaN(formData.quantity) ||
        parseInt(formData.quantity) < 1
      ) {
        showNotify({ type: "warning", message: "请填写正确的数量" });
        return;
      }
      if (!formData.mealPackageText) {
        showNotify({ type: "warning", message: "请选择套餐" });
        return;
      }
      try {
        submitting.value = true;
        const submitData = {
          deliveryAddressId: formData.deliveryAddress,
          deliveryAddress: formData.deliveryAddressText,
          mealPackage: formData.mealPackageText,
          mealPackageId: formData.mealPackageId,
          mealPeriod: formData.mealPeriod,
          userId: state.userId,
          name: state.realName,
          phone: state.phone,
          plannedDiningTimes: formData.plannedDiningTimes.map((item) =>
            dayjs(item).format("YYYY-MM-DD")
          ),
          quantity: parseInt(formData.quantity),
          orgId: formData.orgId,
          orderType: 1
        };

        if (isEdit.value) {
          submitData.id = route.query.id;
          submitData.plannedDiningTime = dayjs(
            submitData.plannedDiningTimes[0]
          ).format("YYYY-MM-DD");
        }


        const api = isEdit.value
          ? updateMealReservation
          : createMealReservation;
        const result = await api(submitData);

        if (result.code === "00") {
          showNotify({
            type: "success",
            message: isEdit.value ? "预约修改成功" : "预约成功",
          });
          router.back();
        } else {
          if(!isEdit.value && result.code === "05") {
              showDialog({
                title: '温馨提示',
                message: result.message,
                showCancelButton: true
              }).then(() => {
                router.push({
                  path: '/meal/reservation',
                  query: {
                    token: localStorage.getItem('token'),
                    userId: state.userId,
                    realName: state.realName,
                    phone: state.phone,
                    type: 0
                  }
                })
              }).catch(() => {
                console.log('取消')
              })
          } else {
            showNotify({ type: "warning", message: result.message });
          }
          
        }
      } catch (error) {
        console.error("提交失败:", error);
        showNotify({ type: "danger", message: "提交失败" });
      } finally {
        submitting.value = false;
      }
    };

    const getDeliveryAddress = async () => {
      // 获取送餐地点选项
      try {
        const res = await postSyparamSelectList({ bigCode: "xiaoqu" });
        if (res && Array.isArray(res.data)) {
          deliveryAddressColumns.value = res.data.map((item) => ({
            text: item.bigDesc,
            value: item.id,
            orgID: item.value,
          }));
        }
      } catch (e) {
        deliveryAddressColumns.value = [];
      }
    };

    const getMealNames = async (isCreate = false) => {
      // 获取套餐选项
      console.log("请求=====formData.plannedDiningTimes>>>>>", formData.plannedDiningTimes);
      try {
        const res = await queryMealPackage({
          times: formData.plannedDiningTimes.map((item) =>
            dayjs(item).format("YYYY-MM-DD")
          ),
          mealPeriod: formData.mealPeriod,
          orgId: formData.orgId,
        });
        if (res && Array.isArray(res.data)) {
          mealPackageColumns.value = res.data.map((item) => ({
            ...item,
            text: item.name,
            value: item.id,
          }));

          if(isCreate) {
            // TODO
            formData.mealPackageId = mealPackageColumns.value?.[0]?.value;
            formData.mealPackageText = mealPackageColumns.value?.[0]?.text;
            choiceTypes.value = mealPackageColumns.value?.[0]?.type;
            if (mealPackageColumns.value?.[0]) {
                getMealStandardList();
            }
          }
        }
      } catch (e) {
        mealPackageColumns.value = [];
      }
    };

    const getMealStandardList = async () => {
      try {
        const result = await queryMealStandardList({
          schedulingDates: formData.plannedDiningTimes.map((item) =>
            dayjs(item).format("YYYY-MM-DD")
          ),
          type: choiceTypes.value,
          orgId: formData.orgId,
          mealPeriod: formData.mealPeriod,
        });
        if (result.code == "00") {
          const map = new Map();
          for (let i = 0; i < result.data.length; i++) {
            const item = result.data[i];
            const _day = dayjs(item.schedulingDate).format("YYYY-MM-DD");
            if (map.has(_day)) {
              const hasList = map.get(_day);
              map.set(_day, [...hasList, item]);
            } else {
              map.set(_day, [item]);
            }
          }

          console.log("map>>>>>", Array.from(map));

          const list = Array.from(map);

          mealDescList.value = list.map((item) => {
            const foods = item[1];
            const foodDes = foods.map((food) => food.dishNames);
            return `${dayjs(item[0]).format("MM月DD号")}套餐：${foodDes.join(
              ","
            )}`;
          });

          console.log("map>>>>>", Array.from(map));
        } else {
        }
      } catch (error) {}
    };

    // watch(
    //   () => [formData.mealPeriod, formData.plannedDiningTimes, formData.deliveryAddress],
    //   ([mealPeriod, plannedDiningTimes,deliveryAddress]) => {
    //     if (
    //       mealPeriod &&
    //       deliveryAddress &&
    //       plannedDiningTimes &&
    //       Array.isArray(plannedDiningTimes) &&
    //       plannedDiningTimes.length > 0
    //     ) {
    //       getMealNames();
    //     }
    //   }
    // );

    const gettimeLimit = async () => {
      // 获取送餐地点选项
      try {
        const res = await postSyparamSelectList({ bigCode: "timeLimit" });
        if (res && Array.isArray(res.data)) {
          console.log(res);
          state.limitTime = res.data[0]?.bigDesc ?? "11:00";
        }
      } catch (e) {
        state.limitTime = "11:00";
      }
    };

    const getQuerySelectUserById = async (isCreate) => {
      try {
        const result = await querySelectUserById({
          id: state.userId,
        });
        if (result.code === "00") {
          console.log("getQuerySelectUserById>>>>>>", result);
          formData.deliveryAddressText = result.data?.officeLocation || "";
          formData.deliveryAddress = result.data?.officelocationId || "";
          const addressInfo = deliveryAddressColumns.value.find(
            (item) => item.value == result.data?.officelocationId
          );
          if (addressInfo && addressInfo.orgID) {
            getSyOrgSendPlace({ id: addressInfo.orgID }).then((res) => {
              formData.diningSchoolText = res.data?.name;
              formData.diningSchool = addressInfo.orgID;
              formData.orgId = addressInfo.orgID || "";
              getMealNames(isCreate);
            });
          }
        }
      } catch (error) {
        console.log("error:>>>", error);
      }
    };

    const handleChoiceMealPackage = () => {
      if (!formData.deliveryAddressText) {
        showNotify({ type: "warning", message: "请选择送餐地点" });
        return;
      }
      if (
        !formData.plannedDiningTimes ||
        formData.plannedDiningTimes.length === 0
      ) {
        showNotify({ type: "warning", message: "请选择送餐日期" });
        return;
      }
      if (!formData.mealPeriodText) {
        showNotify({ type: "warning", message: "请选择餐次" });
        return;
      }
      if (
        !formData.quantity ||
        isNaN(formData.quantity) ||
        parseInt(formData.quantity) < 1
      ) {
        showNotify({ type: "warning", message: "请填写正确的数量" });
        return;
      }
      if (!mealPackageColumns.value.length) {
        showNotify({ type: "warning", message: "暂无套餐" });
        return;
      }
      showMealPackagePicker.value = true;
    };

    onMounted(async () => {
      if (route?.query?.userId) {
        state.userId = route?.query?.userId;
      }
      if (route?.query?.realName) {
        state.realName = route?.query?.realName;
      }
      if (route?.query?.phone) {
        state.phone = route?.query?.phone;
      }
      await getDeliveryAddress();

      if (isEdit.value) {
        const info = JSON.parse(route.query.info);
        getReservationDetail(info);
      } else {
        getQuerySelectUserById(true);
      }
      gettimeLimit();

      if (route?.query?.mealPeriod) {
        formData.mealPeriod = route?.query?.mealPeriod || "";
        formData.mealPeriodText = mealPeriodColumns.find(
          (item) => item.value == route?.query?.mealPeriod
        )?.text;
      }

      if (route?.query?.schedulingDate) {
        formData.plannedDiningTimes = [
          new Date(
            dayjs(route?.query?.schedulingDate).format("YYYY-MM-DD")
          ),
        ];
        console.log("formData.plannedDiningTimes>>>>>", formData.plannedDiningTimes);
        formData.plannedDiningTimesText = dayjs(
          route?.query?.schedulingDate
        ).format("YYYY-MM-DD");
      }

    });

    // 监听送餐地址变化，自动拉取不可选日期
    watch(
      () => formData.orgId,
      (newVal, oldVal) => {
        if (newVal) {
          fetchDisabledDates();
        }
      },
      { immediate: true }
    );

    return {
      formData,
      isEdit,
      submitting,
      loading,
      showDatePicker,
      minDate,
      maxDate,
      showMealTypePicker,
      showMealNamePicker,
      showLocationPicker,
      mealTypeColumns,
      mealNameColumns,
      locationColumns,
      onSubmit,
      showDeliveryAddressPicker,
      showDiningSchoolPicker,
      showPlannedDiningTimesPicker,
      showMealPeriodPicker,
      showMealPackagePicker,
      deliveryAddressColumns,
      diningSchoolColumns,
      mealPeriodColumns,
      mealPackageColumns,
      onPlannedDiningTimesConfirm,
      onDeliveryAddressConfirm,
      onDiningSchoolConfirm,
      onMealPeriodConfirm,
      onMealPackageConfirm,
      choiceTypes,
      changeQuantity,
      mealDescList,
      state,
      handleChoiceMealPackage,
      calendarDayFormatter,
    };
  },
});
</script>

<style lang="scss" scoped>
.meal-reservation-form {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #eaf1f7;
  padding: 0;
  align-items: center;

  .form-card {
    background: #fff;
    border-radius: 7px;
    width: 96vw;
    margin: 12px auto 0 auto;
    position: relative;
    overflow: visible;
    box-shadow: none;

    .loading-wrap {
      margin-top: 40%;
    }
    form {
      width: 100%;
      margin-top: 0;
    }
    .form-row {
      display: flex;
      align-items: center;
      min-height: 48px;
      padding: 0 20px;
      font-size: 16px;
      background: #fff;
      .form-label {
        width: 96px;
        color: #222;
        font-weight: 600;
        flex-shrink: 0;
        text-align: left;
      }
      .form-value {
        flex: 1;
        min-height: 44px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        color: #222;
        font-size: 16px;
        background: transparent;
        &.placeholder {
          color: #c0c4cc;
        }
        &.selectable {
          cursor: pointer;
        }
        &.with-arrow::after {
          content: "";
          display: inline-block;
          width: 16px;
          height: 16px;
          background: url('data:image/svg+xml;utf8,<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 4L10 8L6 12" stroke="%23C0C4CC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>')
            no-repeat center/contain;
          margin-left: 8px;
        }
      }
    }
    .divider {
      height: 1px;
      background: #f2f3f5;
      margin: 0 20px;
    }
    .quantity-input {
      display: flex;
      align-items: center;
      border: 1px solid #e5e6eb;
      background: #fff;
      height: 24px;
      width: 82px;
      overflow: hidden;
      justify-content: space-between;
      .quantity-btn {
        width: 24px;
        height: 24px;
        background: #fff;
        border: none;
        border-radius: 0;
        font-size: 20px;
        color: #222;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        outline: none;
      }
      .quantity-field {
        width: 34px;
        height: 24px;
        border: none;
        background: #fff;
        text-align: center;
        font-size: 18px;
        color: #222;
        outline: none;
      }
      .quantity-divider {
        width: 1px;
        height: 100%;
        background: #e5e6eb;
      }
    }
    .remark-row {
      align-items: flex-start;
      .form-label {
        padding-top: 12px;
      }
      .form-value {
        flex-direction: column;
        align-items: flex-end;
        width: 100%;
        .remark-textarea {
          width: 100%;
          min-height: 64px;
          border-radius: 8px;
          border: 1px solid #e5e6eb;
          background: #f7f8fa;
          padding: 12px;
          font-size: 15px;
          color: #222;
          resize: none;
          outline: none;
        }
        .remark-limit {
          width: 100%;
          text-align: right;
          color: #c0c4cc;
          font-size: 13px;
          margin-top: 2px;
          padding-right: 2px;
        }
      }
    }
  }
  .form-btn-wrap {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    width: calc(100vw - 128px);
    margin: 0 auto;
    z-index: 10;
    padding: 16px 20px 24px 20px;
    display: flex;
    justify-content: center;
    .submit-btn {
      width: 100%;
      height: 44px;
      background: #013152;
      color: #fff;
      border: none;
      font-size: 16px;
      font-weight: 500;
      border: 1px solid #013152;
      letter-spacing: 2px;
      box-shadow: none;
      cursor: pointer;
      border-radius: 4px;
      &:disabled {
        background: #b2d2ff;
        cursor: not-allowed;
      }
    }
  }
  .meal-desc {
    margin: 18px 0;
    color: #4a6783;
    font-size: 15px;
    line-height: 1.7;
    padding: 0 16px;
    text-align: left;
    flex: 1;
    width: calc(100% - 32px);

    .meal-list {
      width: 100%;
    }
  }
  .reservation-tip {
    margin: 0 0 12px 0;
    color: #e67e22;
    // background: #fffbe6;
    // border-left: 4px solid #ffd591;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 1.6;
    width: calc(100% - 24px);
    box-sizing: border-box;
    text-align: left;
  }
}

// van-cell label左 value右
:deep(.van-cell) {
  .van-cell__title {
    min-width: 80px;
    display: flex;
    // font-weight: 500;
    font-size: 17px;
    color: #000000;
    justify-content: flex-start;
    font-family: PingFang SC;
  }

  .van-cell__value {
    text-align: right;
    color: #505050;
    flex: 1;
    justify-content: flex-end;
    display: flex;
    align-items: center;
    // font-weight: 500;
    font-size: 16px;
    font-family: PingFang SC;
  }
}

:deep(.cell-placeholder) .van-cell__value {
  color: #c0c4cc !important;
}
</style> 